// General
import "./detail-signup.scss";
import { useState, useEffect } from "react";
// Services
import {
  useLazyGetAioQuery,
  useUpdateUserProfileMutation,
} from "../../../services/data.service";
import { removeHTMLEscape } from "../../../services/regex.service";
// Static Data
import routeConst from "../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateOccupation,
  updateEducation,
  updateRelationshipStatus,
  updateChildren,
  updateSmoking,
  updateDrinking,
} from "../../../redux/store/signupStore";
import { updateErrorToast } from "../../../redux/store/toastStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// Sentry
import * as Sentry from "@sentry/react";
// Material UI
import { TextField, MenuItem, Button } from "@mui/material";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import useCustomNavigate from "../../utility/custom-hooks/useCustomNavigate-hook";
import IconManager from "../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../../shared/elements/spinner/spinner";
import SignupStepper from "../../shared/elements/signup-stepper/signup-stepper";

const DetailSignup = () => {
  // API variables
  const [
    getAio,
    {
      data: getAioData,
      error: getAioErrorData,
      isFetching: getAioFetching,
      isLoading: getAioLoading,
      isSuccess: getAioSuccess,
      isError: getAioError,
    },
  ] = useLazyGetAioQuery();
  const [
    updateUserProfile,
    {
      data: updateUserProfileData,
      error: updateUserProfileErrorData,
      isLoading: updateUserProfileLoading,
      isSuccess: updateUserProfileSuccess,
      isError: updateUserProfileError,
    },
  ] = useUpdateUserProfileMutation();

  // General variables
  const [occupationFocus, setOccupationFocus] = useState(false);
  const [occupationValid, setOccupationValid] = useState(false);
  const [occupationError, setOccupationError] = useState(false);
  const [educationFocus, setEducationFocus] = useState(false);
  const [educationValid, setEducationValid] = useState(false);
  const [educationError, setEducationError] = useState(false);
  const [relationshipFocus, setRelationshipFocus] = useState(false);
  const [relationshipValid, setRelationshipValid] = useState(false);
  const [relationshipError, setRelationshipError] = useState(false);
  const [childrenFocus, setChildrenFocus] = useState(false);
  const [childrenValid, setChildrenValid] = useState(false);
  const [childrenError, setChildrenError] = useState(false);
  const [smokingFocus, setSmokingFocus] = useState(false);
  const [smokingValid, setSmokingValid] = useState(false);
  const [smokingError, setSmokingError] = useState(false);
  const [drinkingFocus, setDrinkingFocus] = useState(false);
  const [drinkingValid, setDrinkingValid] = useState(false);
  const [drinkingError, setDrinkingError] = useState(false);

  // Redux variables
  const occupation = useSelector((state) => state.signup.occupation);
  const education = useSelector((state) => state.signup.education);
  const relationshipStatus = useSelector(
    (state) => state.signup.relationshipStatus
  );
  const children = useSelector((state) => state.signup.children);
  const smoking = useSelector((state) => state.signup.smoking);
  const drinking = useSelector((state) => state.signup.drinking);
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const onNavigate = useCustomNavigate();
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-18-18.3",
      },
    });

    getAio(null, true);
  }, []);

  // Lifecycle | Check for update | Update User Profile API Response
  useEffect(() => {
    if (updateUserProfileLoading) {
    } else if (updateUserProfileSuccess) {
      onNavigate(routeConst.signupSuccess.path);
    } else if (updateUserProfileError) {
      switch (updateUserProfileErrorData?.data?.status) {
        case -1:
          const errorToast = {
            message: updateUserProfileErrorData?.data?.message,
            autoClose: 3000,
          };
          dispatch(updateErrorToast(errorToast));
          break;
        default:
          break;
      }
    }
  }, [
    updateUserProfileLoading,
    updateUserProfileSuccess,
    updateUserProfileError,
  ]);

  // Event Handlers | Button
  const onSaveDetails = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-18-18.3-18.3.1-Button",
      },
    });

    const obj = {
      children,
      drinking,
      education,
      occupation,
      relationship: relationshipStatus,
      smoking,
    };
    updateUserProfile(obj);
  };
  const onSkip = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-18-18.3-18.3.2-Button",
      },
    });

    onNavigate(routeConst.signupSuccess.path);
  };

  // Event Handlers | MUI Select
  const handleSelectChange = (type) => (event) => {
    switch (type) {
      case "occupation":
        dispatch(updateOccupation(event.target.value));
        break;
      case "education":
        dispatch(updateEducation(event.target.value));
        break;
      case "relationship":
        dispatch(updateRelationshipStatus(event.target.value));
        break;
      case "children":
        dispatch(updateChildren(event.target.value));
        break;
      case "smoking":
        dispatch(updateSmoking(event.target.value));
        break;
      case "drinking":
        dispatch(updateDrinking(event.target.value));
        break;
      default:
        break;
    }
  };
  const onOccupationFocus = () => {
    setOccupationFocus(true);
  };
  const onEducationFocus = () => {
    setEducationFocus(true);
  };
  const onRelationshipFocus = () => {
    setRelationshipFocus(true);
  };
  const onChildrenFocus = () => {
    setChildrenFocus(true);
  };
  const onSmokingFocus = () => {
    setSmokingFocus(true);
  };
  const onDrinkingFocus = () => {
    setDrinkingFocus(true);
  };

  // Utility Functions
  const isFormValid = () => {
    return (
      occupationValid &&
      educationValid &&
      relationshipValid &&
      childrenValid &&
      smokingValid &&
      drinkingValid
    );
  };
  const occupationValidation = () => {
    let valid = true;
    let error = false;

    if (occupation === "") {
      valid = false;
      error = true;
    } else if (removeHTMLEscape.test(occupation)) {
      valid = false;
      error = true;
      dispatch(updateOccupation(""));

      // Send to Sentry
      Sentry.captureMessage(
        "PWA-18-18.3-Occupation-Input-HTML-Escape-Detected"
      );

      // Send to GTM
      TagManager.dataLayer({
        dataLayer: {
          event: "PWA-18-18.3-Occupation-Input-HTML-Escape-Detected",
        },
      });
    } else {
      valid = true;
      error = false;
    }

    setOccupationValid(valid);
    setOccupationError(error);
  };
  const educationValidation = () => {
    let valid = true;
    let error = false;

    if (education === "") {
      valid = false;
      error = true;
    } else {
      valid = true;
      error = false;
    }

    setEducationValid(valid);
    setEducationError(error);
  };
  const relationshipValidation = () => {
    let valid = true;
    let error = false;

    if (relationshipStatus === "") {
      valid = false;
      error = true;
    } else {
      valid = true;
      error = false;
    }

    setRelationshipValid(valid);
    setRelationshipError(error);
  };
  const childrenValidation = () => {
    let valid = true;
    let error = false;

    if (children === "") {
      valid = false;
      error = true;
    } else {
      valid = true;
      error = false;
    }

    setChildrenValid(valid);
    setChildrenError(error);
  };
  const smokingValidation = () => {
    let valid = true;
    let error = false;

    if (smoking === "") {
      valid = false;
      error = true;
    } else {
      valid = true;
      error = false;
    }

    setSmokingValid(valid);
    setSmokingError(error);
  };
  const drinkingValidation = () => {
    let valid = true;
    let error = false;

    if (drinking === "") {
      valid = false;
      error = true;
    } else {
      valid = true;
      error = false;
    }

    setDrinkingValid(valid);
    setDrinkingError(error);
  };

  // Check for text field validation
  useEffect(() => {
    occupationValidation();
    educationValidation();
    relationshipValidation();
    childrenValidation();
    smokingValidation();
    drinkingValidation();
  }, [
    occupation,
    occupationFocus,
    education,
    educationFocus,
    relationshipStatus,
    relationshipFocus,
    children,
    childrenFocus,
    smoking,
    smokingFocus,
    drinking,
    drinkingFocus,
  ]);

  if (getAioFetching || getAioLoading) {
    return <Spinner />;
  } else if (getAioSuccess) {
    return (
      <div id="detail-signup-page">
        <div className="max-width-container">
          <div className="navbar-container">
            <div
              className="back-button"
              onClick={() => onNavigate(routeConst.lifestyleSignup.path)}
            >
              {getIcon("signupBackIcon", "signup-back-icon")}
            </div>
          </div>

          <div className="overflow-container">
            <div className="stepper-container">
              <div className="stepper-max-width-container">
                <div className="stepper-max-width-inner-container">
                  <SignupStepper current={5} total={5} />
                </div>
              </div>
            </div>

            <div className="step-description-label">
              {t("signup.youre_almost_finished")}
            </div>

            <div className="form-field-container">
              <div className="occupation-form-field-container">
                <div className="occupation-label">
                  {t("profile.occupation")}
                </div>

                <TextField
                  className="occupation-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                  value={occupation}
                  error={occupationError}
                  onChange={handleSelectChange("occupation")}
                  variant="outlined"
                  onFocus={onOccupationFocus}
                ></TextField>
              </div>

              <div className="education-form-field-container">
                <div className="education-label">{t("profile.education")}</div>

                <TextField
                  className="education-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                  select
                  value={education}
                  error={educationError}
                  onChange={handleSelectChange("education")}
                  variant="outlined"
                  onFocus={onEducationFocus}
                >
                  {getAioData?.payload?.dropdown?.education?.map(
                    (education, index) => (
                      <MenuItem value={education?.value} key={index}>
                        {t(`${education?.name}`)}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </div>

              <div className="flex-container">
                <div className="relationship-form-field-container">
                  <div className="relationship-label">
                    {t("profile.relationship")}
                  </div>

                  <TextField
                    className="relationship-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                    select
                    value={relationshipStatus}
                    error={relationshipError}
                    onChange={handleSelectChange("relationship")}
                    variant="outlined"
                    onFocus={onRelationshipFocus}
                  >
                    {getAioData?.payload?.dropdown?.relationship?.map(
                      (relationship, index) => (
                        <MenuItem value={relationship?.value} key={index}>
                          {t(`${relationship.name}`)}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </div>

                <div className="children-form-field-container">
                  <div className="children-label">{t("profile.children")}</div>

                  <TextField
                    className="children-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                    select
                    value={children}
                    error={childrenError}
                    onChange={handleSelectChange("children")}
                    variant="outlined"
                    onFocus={onChildrenFocus}
                  >
                    {getAioData?.payload?.dropdown?.children?.map(
                      (children, index) => (
                        <MenuItem value={children?.value} key={index}>
                          {children?.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </div>
              </div>

              <div className="flex-container">
                <div className="smoking-form-field-container">
                  <div className="smoking-label">{t("profile.smoking")}</div>

                  <TextField
                    className="smoking-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                    select
                    value={smoking}
                    error={smokingError}
                    onChange={handleSelectChange("smoking")}
                    variant="outlined"
                    onFocus={onSmokingFocus}
                  >
                    {getAioData?.payload?.dropdown?.smoking?.map(
                      (smoking, index) => (
                        <MenuItem value={smoking?.value} key={index}>
                          {t(`${smoking?.name}`)}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </div>

                <div className="drinking-form-field-container">
                  <div className="drinking-label">{t("profile.drinking")}</div>

                  <TextField
                    className="drinking-form-field custom-text-field custom-text-field-max-radius custom-text-field-outline-default custom-text-field-outline-background-color"
                    select
                    value={drinking}
                    error={drinkingError}
                    onChange={handleSelectChange("drinking")}
                    variant="outlined"
                    onFocus={onDrinkingFocus}
                  >
                    {getAioData?.payload?.dropdown?.drinking?.map(
                      (drinking, index) => (
                        <MenuItem value={drinking?.value} key={index}>
                          {t(`${drinking?.name}`)}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </div>
              </div>
            </div>
          </div>

          <div className="bottom-container">
            <div className="button-container">
              <Button
                className={`next-button ${
                  isFormValid() ? "" : "disabled-button"
                }`}
                onClick={onSaveDetails}
                disabled={!isFormValid()}
              >
                {updateUserProfileLoading ? (
                  <Spinner
                    size={21}
                    isPadding={false}
                    color={"white-spinner"}
                  />
                ) : (
                  t("signup.next")
                )}
              </Button>

              <Button className="skip-button" onClick={onSkip}>
                {t("signup.skip_for_now")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (getAioError) {
  }
};

export default DetailSignup;
